import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useApiMutation } from '../../../shared/useApi.js';

const KEY = ['suppliers'];

// مسارات حسابات الموردين تُعيد { items, total, stats, mode, notice } — وهذا
// مغلّف صفحة، فيفكّه مُعترض shared/api.js إلى مصفوفة ويُلصق باقي الحقول
// عليها. نعيد تغليفه هنا في كائن صريح حتى لا تعتمد الشاشة على ذلك السلوك.
function unwrapList(data) {
  const items = Array.isArray(data) ? data : (data?.items || []);
  const src = Array.isArray(data) ? data : (data || {});
  return {
    items,
    total: src.total ?? items.length,
    page: src.page ?? 1,
    pages: src.pages ?? 1,
    mode: src.mode || 'page',
    truncated: !!src.truncated,
    cap: src.cap ?? 0,
    notice: src.notice || '',
    stats: src.stats || null
  };
}

export function useSupplierAccounts(params) {
  return useQuery({
    queryKey: [...KEY, 'accounts', params],
    queryFn: async () => {
      const { data } = await api.get('/suppliers', { params });
      return unwrapList(data);
    },
    keepPreviousData: true,
    staleTime: 10_000
  });
}

export function useSupplierAccount(supplierId, params = {}) {
  return useQuery({
    queryKey: [...KEY, 'account', supplierId, params],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data } = await api.get(`/suppliers/${supplierId}`, { params });
      return data;
    },
    staleTime: 5_000
  });
}

export function useSupplierStatement(supplierId, params, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'statement', supplierId, params],
    enabled: !!supplierId && enabled,
    queryFn: async () => {
      const { data } = await api.get(`/suppliers/${supplierId}/statement`, { params });
      return data;
    },
    staleTime: 5_000
  });
}

export function useSupplierPayments(params, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'payments', params],
    enabled,
    queryFn: async () => {
      const { data } = await api.get('/suppliers/payments', { params });
      return unwrapList(data);
    },
    staleTime: 10_000
  });
}

/** تقارير الموردين الخمسة — نفس الشكل: { rows, totals, count }. */
export function useSupplierReport(kind, params, enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'report', kind, params],
    enabled: !!kind && enabled,
    queryFn: async () => {
      const { data } = await api.get(`/suppliers/reports/${kind}`, { params });
      return data || { rows: [], totals: {}, count: 0 };
    },
    staleTime: 10_000
  });
}

export function useCashboxes() {
  return useQuery({
    queryKey: ['cashboxes', 'suppliers'],
    queryFn: async () => {
      const { data } = await api.get('/cashbox');
      const list = data?.cashboxes || data?.data || data || [];
      return (Array.isArray(list) ? list : []).filter((cb) => cb.isActive !== false);
    },
    staleTime: 30_000
  });
}

function invalidate(qc) {
  qc.invalidateQueries({ queryKey: KEY });
  // التسديد النقدي يغيّر رصيد القاصة ونقد الشفت المفتوح والمشتريات.
  qc.invalidateQueries({ queryKey: ['purchases'] });
  qc.invalidateQueries({ queryKey: ['shift'] });
  qc.invalidateQueries({ queryKey: ['shifts'] });
  qc.invalidateQueries({ queryKey: ['cashboxes'] });
  qc.invalidateQueries({ queryKey: ['catalog', 'suppliers'] });
}

export function useSettleSupplier() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/suppliers/settle', body).then((r) => r.data),
    onSuccess: () => invalidate(qc)
  });
}

export function useRefundSupplier() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body) => api.post('/suppliers/refund', body).then((r) => r.data),
    onSuccess: () => invalidate(qc)
  });
}

export function useReverseVoucher() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ paymentId, reason }) =>
      api.post(`/suppliers/payments/${paymentId}/reverse`, { reason }).then((r) => r.data),
    successMessage: 'تم تسجيل قيد عكسي للسند',
    onSuccess: () => invalidate(qc)
  });
}

export function useMigrateLegacy() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (body = {}) => api.post('/suppliers/migrate-legacy', body).then((r) => r.data),
    onSuccess: () => invalidate(qc)
  });
}
